import { SupabaseClient } from "@supabase/supabase-js";

/**
 * Utility functions to help debug Supabase configuration issues
 */

export const hasValidSupabaseConfig = () => {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
  
  if (!url || !key) {
    console.warn('Missing Supabase configuration: URL or API key is undefined');
    return false;
  }
  
  if (url.includes('your-project-url') || key.includes('your-anon-key')) {
    console.warn('Invalid Supabase configuration: Using placeholder values');
    return false;
  }
  
  // Added log to debug valid configuration
  console.log('Supabase configuration appears valid');
  return true;
};

export const debugSupabaseConfig = () => {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
  
  const issues = [];
  
  // Check if URL is defined
  if (!url) {
    issues.push("VITE_SUPABASE_URL is not defined in .env file");
  } else if (url.includes('your-project-url')) {
    issues.push("VITE_SUPABASE_URL contains placeholder value 'your-project-url'");
  } else if (!url.includes('supabase.co')) {
    issues.push("VITE_SUPABASE_URL doesn't appear to be a valid Supabase URL (should contain 'supabase.co')");
  }
  
  // Check if key is defined
  if (!key) {
    issues.push("VITE_SUPABASE_ANON_KEY is not defined in .env file");
  } else if (key.includes('your-anon-key')) {
    issues.push("VITE_SUPABASE_ANON_KEY contains placeholder value 'your-anon-key'");
  } else if (!key.startsWith('eyJ')) {
    issues.push("VITE_SUPABASE_ANON_KEY doesn't appear to be a valid JWT token (should start with 'eyJ')");
  } else if (key.length < 100) {
    issues.push("VITE_SUPABASE_ANON_KEY appears to be too short for a valid Supabase key");
  }
  
  return {
    url: url ? `${url.substring(0, 8)}...` : 'Not defined in .env file',
    key: key ? `${key.substring(0, 10)}...` : 'Not defined in .env file',
    hasIssues: issues.length > 0,
    issues,
  };
};

// Type definition for connection test response
interface ConnectionTestResponse {
  success: boolean;
  latency?: number;
  error?: string;
  message?: string;
}

/**
 * Tests connectivity to Supabase by attempting to get a bucket
 */
export const testSupabaseConnection = async (): Promise<ConnectionTestResponse> => {
  try {
    const { supabase } = await import('./supabase');
    
    const startTime = Date.now();
    
    try {
      const response = await supabase.storage.getBucket('test-bucket');
      const endTime = Date.now();
      const latency = endTime - startTime;
      
      // If we get an error about bucket not found, that's actually good
      // it means we connected to Supabase but the bucket doesn't exist
      if (response.error && (
          response.error.message.includes('not found') || 
          response.error.message.includes('does not exist')
      )) {
        return { 
          success: true, 
          latency,
          message: "Successfully connected to Supabase (bucket doesn't exist, which is expected)"
        };
      }
      
      return {
        success: true,
        latency,
        message: "Successfully connected to Supabase"
      };
    } catch (err: any) {
      // Special handling for the case where "bucket not found" error is thrown
      if (err.message && (
          err.message.includes('not found') || 
          err.message.includes('does not exist')
      )) {
        const endTime = Date.now();
        return { 
          success: true, 
          latency: endTime - startTime,
          message: "Successfully connected to Supabase (bucket doesn't exist, which is expected)"
        };
      }
      throw err;
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || "Unknown error",
      message: "Failed to connect to Supabase. Check your .env file configuration."
    };
  }
};

/**
 * Debug initialization status to help diagnose issues
 */
export const logInitializationStatus = (isInitialized: boolean, message?: string) => {
  console.log(`[Supabase] Initialization status: ${isInitialized ? 'Initialized' : 'Not initialized'}${message ? ` - ${message}` : ''}`);
  
  // Check if config is valid when not initialized
  if (!isInitialized) {
    const configStatus = hasValidSupabaseConfig();
    console.log(`[Supabase] Config validity check: ${configStatus ? 'Valid' : 'Invalid'}`);
  }
};
